/**
 * @file 活动编辑全屏弹窗-工具
 */

import { Toast } from '@seiue/ui'
import { createEnumHelper, noop } from '@seiue/util'
import { Upload } from 'antd'

import { TaskStatusEnum, TaskTypeEnum, fileApi } from 'packages/sdks-next/growp'

import { openEditAnnouncementTask } from '../Tasks/Announcement'
import { openEditCourseTask } from '../Tasks/Course'
import { openEditEnrollTask } from '../Tasks/Enroll'
import { openEditInterviewTask } from '../Tasks/Interview'
import { openEditLiveTask } from '../Tasks/Live'
import { openEditQuestionnaireTask } from '../Tasks/Questionnaire'
import { openEditQuizTask } from '../Tasks/Quiz'
import { openEditWaggleTask } from '../Tasks/Waggle'

/**
 * 图片上传
 *  - 限制图片类型
 *
 * @param file - 文件
 * @param fileSizeLimit - 文件大小
 * @returns 上传结果
 */
export const uploadPngOrJpg = async (file: File, fileSizeLimit: number) => {
  const fileSizeM = file.size / 1024 / 1024
  if (fileSizeLimit > 0 && fileSizeM > fileSizeLimit) {
    Toast.error(`请上传体积小于${fileSizeLimit}M的附件`)
    return Upload.LIST_IGNORE
  }

  const { data } = await fileApi.fileUpload(
    `${file.name.substring(
      0,
      file.name.lastIndexOf('.')
    )}${Date.now()}.${file.type.substring(6)}`,
    { file }
  )

  return data.url
}

/**
 * 任务类型映射文本
 */
export const taskTypeEnumToLabel = createEnumHelper(() => ({
  [TaskTypeEnum.Questionnaire]: '问卷',
  [TaskTypeEnum.Quiz]: '测验',
  [TaskTypeEnum.Course]: '课程',
  [TaskTypeEnum.Announcement]: '公告',
  [TaskTypeEnum.Live]: '直播',
  [TaskTypeEnum.Enroll]: '报名',
  [TaskTypeEnum.Interview]: '面试',
  [TaskTypeEnum.Waggle]: '摇号',
  [TaskTypeEnum.Pdf]: '其他',
  [TaskTypeEnum.Video]: '其他',
  [TaskTypeEnum.Exercise]: '其他',
}))

/**
 * 任务类型映射图标名
 */
export const taskTypeEnumToIconName = createEnumHelper(() => ({
  [TaskTypeEnum.Questionnaire]: 'Wenjuan',
  [TaskTypeEnum.Quiz]: 'Ceyan',
  [TaskTypeEnum.Course]: 'Kecheng',
  [TaskTypeEnum.Announcement]: 'Gonggao',
  [TaskTypeEnum.Live]: 'Zhibo',
  [TaskTypeEnum.Enroll]: 'Baoming',
  [TaskTypeEnum.Interview]: 'Mianshi',
  [TaskTypeEnum.Waggle]: 'Yaohao',
  [TaskTypeEnum.Pdf]: 'Yaohao',
  [TaskTypeEnum.Video]: 'Yaohao',
  [TaskTypeEnum.Exercise]: 'Yaohao',
}))

/**
 * 任务类型映射图标默认颜色
 */
export const taskTypeEnumToIconColor = createEnumHelper(() => ({
  [TaskTypeEnum.Questionnaire]: '#FFA52E',
  [TaskTypeEnum.Quiz]: '#00CC36',
  [TaskTypeEnum.Course]: '#F78644',
  [TaskTypeEnum.Announcement]: '#9D4FE6',
  [TaskTypeEnum.Live]: '#08CCBF',
  [TaskTypeEnum.Enroll]: '#1D9D94',
  [TaskTypeEnum.Interview]: '#F76560',
  [TaskTypeEnum.Waggle]: '#1FB4FF',
  [TaskTypeEnum.Pdf]: '#fff',
  [TaskTypeEnum.Video]: '#fff',
  [TaskTypeEnum.Exercise]: '#fff',
}))

/**
 * 任务类型映射默认背景颜色
 */
export const taskTypeEnumToBgColor = createEnumHelper(() => ({
  [TaskTypeEnum.Questionnaire]: '#FFF8E8',
  [TaskTypeEnum.Quiz]: '#E8FFEA',
  [TaskTypeEnum.Course]: '#FFF4E8',
  [TaskTypeEnum.Announcement]: '#F7E8FF',
  [TaskTypeEnum.Live]: '#E8FFFA',
  [TaskTypeEnum.Enroll]: '#E8FFFA',
  [TaskTypeEnum.Interview]: '#FFECE8',
  [TaskTypeEnum.Waggle]: '#E8FBFF',
  [TaskTypeEnum.Pdf]: '#fff',
  [TaskTypeEnum.Video]: '#fff',
  [TaskTypeEnum.Exercise]: '#fff',
}))

/**
 * 任务类型映射文本
 */
export const taskStatusEnumToLabel = createEnumHelper(() => ({
  [TaskStatusEnum.Published]: '已发布',
  [TaskStatusEnum.Editing]: '编辑中',
}))

/**
 * 任务开始方式转文本
 *
 * @param startAt - 立即开始时间
 * @param timedStartAt - 定时开始时间
 * @param prevTaskIds - 前置任务
 * @returns 文本格式
 */
export const taskOpenWayToLabel = (
  startAt?: string | null,
  timedStartAt?: string | null,
  prevTaskIds?: number[]
): string => {
  const hasTask = prevTaskIds !== undefined && prevTaskIds.length > 0

  if (timedStartAt && !hasTask) {
    return `定时（${timedStartAt.slice(0, 16)}）`
  } else if (timedStartAt && hasTask) {
    return '定时+条件'
  } else if (!timedStartAt && hasTask) {
    return '条件'
  }

  return startAt ? '默认开启' : '暂不开启'
}

/**
 * 任务结束方式转文本
 *
 * @param endAt - 结束时间
 * @returns 文本格式
 */
export const taskCloseWayToLabel = (endAt?: string | null): string =>
  endAt ? `定时（${endAt.slice(0, 16)}）` : '暂不设置'

/**
 * 获取活动任务可显示类型
 *
 * @returns 任务类型数组
 */
export const taskShowTypes = [
  TaskTypeEnum.Questionnaire,
  TaskTypeEnum.Quiz,
  TaskTypeEnum.Course,
  TaskTypeEnum.Announcement,
  TaskTypeEnum.Live,
  TaskTypeEnum.Enroll,
  TaskTypeEnum.Interview,
  TaskTypeEnum.Waggle,
]

export const taskTypeToOpenMethod = createEnumHelper(() => ({
  [TaskTypeEnum.Questionnaire]: openEditQuestionnaireTask,
  [TaskTypeEnum.Course]: openEditCourseTask,
  [TaskTypeEnum.Announcement]: openEditAnnouncementTask,
  [TaskTypeEnum.Live]: openEditLiveTask,
  [TaskTypeEnum.Enroll]: openEditEnrollTask,
  [TaskTypeEnum.Interview]: openEditInterviewTask,
  [TaskTypeEnum.Waggle]: openEditWaggleTask,
  [TaskTypeEnum.Pdf]: noop,
  [TaskTypeEnum.Video]: noop,
  [TaskTypeEnum.Exercise]: noop,
  [TaskTypeEnum.Quiz]: openEditQuizTask,
}))
